import { Card, Row, Tag } from 'antd';
import i18next from 'i18next';
import Moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { PermissionsConstants } from 'src/common/types';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { useIdentity } from 'src/core/services/authentication';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { container } from 'src/inversify.config';
import { AuditLogSummary, AuditLogSummaryStore } from 'src/stores/audit-logs-store';
import { ExportImportLogType } from 'src/stores/organizations-store';
const permissions = [PermissionsConstants.ManageAdminOrganization];

const OrganizationExportImportLogs: FC = () => {
  const identity = useIdentity();
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const hasManageAdminOrganitionsPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageAdminOrganization);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'timestamp', direction: 'Descending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const getFilteredQuery = (newQuery: Query) => {
    let filter = '';
    for (const logType of Object.values(ExportImportLogType)) {
      filter += ` or type eq '${logType}'`;
    }
    if (newQuery.filter && (newQuery as any).filter['Type']) {
      filter = `type eq '${(newQuery as any).filter['Type']}'`;
    } else {
      filter = filter.substring(3);
    }
    return { ...newQuery, filter: [filter] };
  };

  const exportImportLogsStore = container.get(AuditLogSummaryStore);
  const exportImportLogsState = exportImportLogsStore.state;

  useEffect(
    () => {
      if (!hasManageAdminOrganitionsPermission) return;
      exportImportLogsStore.load(getFilteredQuery(query));
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (!hasManageAdminOrganitionsPermission) {
    navigate('/organizations');
    return null;
  }

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'Timestamp',
        field: 'Timestamp',
        title: t('CreatedOn'),
        renderer: (data) => {
          const formattedDate = Moment.utc(data.timestamp).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`);
          return <span>{formattedDate}</span>;
        },
      },
      {
        sortable: true,
        searchable: true,
        sortField: 'UserDisplayName',
        useProfile: true,
        field: 'UserDisplayName',
        title: t('CreatedBy'),
        renderer: (data) => <span>{data.userDisplayName}</span>,
      },
      {
        sortable: true,
        searchable: true,
        sortField: 'OrganizationDisplayName',
        useProfile: true,
        field: 'OrganizationDisplayName',
        title: t('OrganizationName'),
        renderer: (data) => <span>{data.organizationDisplayName}</span>,
      },
      {
        sortable: true,
        searchable: false,
        sortField: 'Type',
        width: '5%',
        filter: (
          <OptionsFilter
            onChange={async () => {
              await new Promise((resolve) => setTimeout(resolve, 1000));
            }}
            options={[
              { label: t('BackupStart'), value: 'BackupStart' },
              { label: t('BackupSuccess'), value: ExportImportLogType.BackupSuccess },
              { label: t('BackupError'), value: ExportImportLogType.BackupError },
              { label: t('RestoreBackupStart'), value: ExportImportLogType.RestoreBackupStart },
              { label: t('RestoreBackupSuccess'), value: ExportImportLogType.RestoreBackupSuccess },
              { label: t('RestoreBackupError'), value: ExportImportLogType.RestoreBackupError },
            ]}
          />
        ),
        field: 'Type',
        title: t('LogType'),
        renderer: (data) => <Tag>{data.type}</Tag>,
      },
      {
        sortable: true,
        searchable: true,
        width: '40%',
        sortField: 'Description',
        useProfile: true,
        field: 'Description',
        title: t('Description'),
        renderer: (data) => <span>{t(data.description)}</span>,
      },
    ],
    data: { ...exportImportLogsState.value, isBusy: exportImportLogsState.value.isBusy },
  } as TableModel<AuditLogSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(getFilteredQuery(query));
            exportImportLogsStore.load(getFilteredQuery(query));
          }}
          onRefresh={() => exportImportLogsStore.load(getFilteredQuery(query))}
          model={exportImportLogsState.errorMessage.value ? emptyTableModel : tableModel}
          error={exportImportLogsState.errorMessage.value && formatMessage(exportImportLogsState.errorMessage.value)}
        />
      </Card>
    </Row>
  );
};

export default OrganizationExportImportLogs;
